/**
 * Inspectable input references and targeted reuse over the existing records.
 *
 * These are projections, not a second evidence ledger. A span can prove that
 * words were supplied; it cannot prove their interpretation, admit a citation,
 * or stand in for a person reviewing a consequential field.
 */

import type { ClaimScope } from './contradiction'
import { corpusEvidenceRecordId, evidenceBindingId, webEvidenceRecordId } from './evidenceIds'
import { pendingEvidenceStaleOf } from './rebase'
import { fnv1a64, foldText, textHash } from './similarity'
import { refutedUnder, refutedUnderSentence, taintOf } from './taint'
import type { ModelStamp, Session, SourceReading } from './types'
import { verificationLive } from './types'
import { STATUS_WORD } from './vocabulary'

export const EVIDENCE_CONTINUITY_VERSION = 1 as const

export interface ContentRevision {
  algorithm: 'fnv1a64-utf16-v1'
  digest: string
  chars: number
}

/** Exact input, without case or whitespace folding; this is not a signature of authenticity. */
export function contentRevision(text: string): ContentRevision {
  return { algorithm: 'fnv1a64-utf16-v1', digest: fnv1a64(text), chars: text.length }
}

function canonical(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`
  if (value && typeof value === 'object') {
    const row = value as Record<string, unknown>
    return `{${Object.keys(row)
      .filter((key) => row[key] !== undefined)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${canonical(row[key])}`)
      .join(',')}}`
  }
  return JSON.stringify(value) ?? 'null'
}

const revisionOf = (value: unknown) => contentRevision(canonical(value))
const same = (left: unknown, right: unknown) => canonical(left) === canonical(right)

export interface RetainedSpan {
  origin: 'claim' | 'research-prompt' | 'web-quote' | 'corpus-quote' | 'evidence-record'
  /** Existing node, operation, binding, or record id, never an invented source id. */
  originId: string
  revision: ContentRevision
  /** Offsets into the exact retained origin, in UTF-16 code units. */
  start: number
  end: number
  excerpt: string
  locator: string
}

export const SCOPE_FIELDS = [
  'population',
  'intervention',
  'exposure',
  'comparator',
  'outcome',
  'measurement',
  'timeHorizon',
  'studyDesign',
  'definition',
  'causalMode',
  'direction',
  'nullMeaning',
  'assumptions',
  'quantitative.estimate',
  'quantitative.estimand',
  'quantitative.unit',
  'quantitative.nullValue',
  'quantitative.interval',
  'quantitative.equivalenceBounds',
] as const
export type ScopeField = (typeof SCOPE_FIELDS)[number]

export interface ScopeFieldOrigin {
  version: typeof EVIDENCE_CONTINUITY_VERSION
  field: ScopeField
  claimRevision: ContentRevision
  valueRevision: ContentRevision
  span: RetainedSpan | null
  extraction: 'model-proposal'
  review: 'unreviewed'
}

function fieldValue(scope: ClaimScope, field: ScopeField): unknown {
  if (field.startsWith('quantitative.')) {
    const key = field.slice('quantitative.'.length) as keyof NonNullable<ClaimScope['quantitative']>
    return scope.quantitative?.[key]
  }
  return scope[field as Exclude<ScopeField, `quantitative.${string}`>]
}

function populatedFields(scope: ClaimScope): ScopeField[] {
  return SCOPE_FIELDS.filter((field) => {
    const value = fieldValue(scope, field)
    return (
      value !== undefined &&
      value !== null &&
      value !== '' &&
      value !== 'unknown' &&
      value !== 'unspecified' &&
      (!Array.isArray(value) || value.length > 0)
    )
  })
}

export interface ScopeOriginProposal {
  field: ScopeField
  basis: 'claim' | 'supplied-context'
  excerpt: string
  locator: string
}

/** The host supplies every revision and offset; a model supplies only an excerpt proposal. */
export function anchorScopeFields(
  scope: ClaimScope,
  proposals: readonly ScopeOriginProposal[],
  input: { nodeId: string; claim: string; context?: string; prompt: string; opId: string },
): ScopeFieldOrigin[] {
  const known = new Set(populatedFields(scope))
  const byField = new Map<ScopeField, RetainedSpan>()
  for (const proposal of proposals) {
    if (!known.has(proposal.field) || byField.has(proposal.field)) {
      throw new Error(`Field origin names an absent or repeated field: ${proposal.field}.`)
    }
    const material = proposal.basis === 'claim' ? input.claim : (input.context ?? '')
    const start = material.indexOf(proposal.excerpt)
    if (
      !proposal.excerpt ||
      proposal.excerpt.length > 6000 ||
      proposal.locator.length > 1000 ||
      start < 0 ||
      material.indexOf(proposal.excerpt, start + 1) >= 0
    ) {
      throw new Error(
        `Field origin for ${proposal.field} needs one exact, unambiguous supplied excerpt.`,
      )
    }
    // A context is the exact suffix the host appended. Searching the prompt for
    // a delimiter would let a supplied claim masquerade as another input section.
    if (proposal.basis === 'supplied-context' && !input.prompt.endsWith(material)) {
      throw new Error('Supplied context is not retained in the extraction prompt.')
    }
    const origin = proposal.basis === 'claim' ? input.claim : input.prompt
    const offset = proposal.basis === 'claim' ? 0 : input.prompt.length - material.length
    byField.set(proposal.field, {
      origin: proposal.basis === 'claim' ? 'claim' : 'research-prompt',
      originId: proposal.basis === 'claim' ? input.nodeId : input.opId,
      revision: contentRevision(origin),
      start: offset + start,
      end: offset + start + proposal.excerpt.length,
      excerpt: proposal.excerpt,
      locator: proposal.locator,
    })
  }
  return [...known].map((field) => ({
    version: EVIDENCE_CONTINUITY_VERSION,
    field,
    claimRevision: contentRevision(input.claim),
    valueRevision: revisionOf(fieldValue(scope, field)),
    span: byField.get(field) ?? null,
    extraction: 'model-proposal',
    review: 'unreviewed',
  }))
}

const object = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null

function parsedRevision(value: unknown): ContentRevision | null {
  const row = object(value)
  return row?.algorithm === 'fnv1a64-utf16-v1' &&
    typeof row.digest === 'string' &&
    /^[a-f0-9]{16}$/.test(row.digest) &&
    typeof row.chars === 'number' &&
    Number.isSafeInteger(row.chars) &&
    row.chars >= 0
    ? { algorithm: row.algorithm, digest: row.digest, chars: row.chars }
    : null
}

/** Old or malformed origins stay absent. No imported state can claim a human review here. */
export function normalizeScopeFieldOrigins(value: unknown): ScopeFieldOrigin[] | undefined {
  if (!Array.isArray(value)) return undefined
  const out: ScopeFieldOrigin[] = []
  const seen = new Set<string>()
  for (const raw of value.slice(0, SCOPE_FIELDS.length)) {
    const row = object(raw)
    if (
      !row ||
      row.version !== 1 ||
      row.extraction !== 'model-proposal' ||
      row.review !== 'unreviewed' ||
      typeof row.field !== 'string' ||
      !SCOPE_FIELDS.includes(row.field as ScopeField) ||
      seen.has(row.field)
    )
      continue
    const claimRevision = parsedRevision(row.claimRevision)
    const valueRevision = parsedRevision(row.valueRevision)
    if (!claimRevision || !valueRevision) continue
    let span: RetainedSpan | null = null
    const rawSpan = object(row.span)
    if (rawSpan) {
      const revision = parsedRevision(rawSpan.revision)
      if (
        revision &&
        (rawSpan.origin === 'claim' || rawSpan.origin === 'research-prompt') &&
        typeof rawSpan.originId === 'string' &&
        rawSpan.originId.length > 0 &&
        rawSpan.originId.length <= 240 &&
        typeof rawSpan.excerpt === 'string' &&
        rawSpan.excerpt.length > 0 &&
        rawSpan.excerpt.length <= 6000 &&
        typeof rawSpan.locator === 'string' &&
        rawSpan.locator.length <= 1000 &&
        typeof rawSpan.start === 'number' &&
        Number.isSafeInteger(rawSpan.start) &&
        rawSpan.start >= 0 &&
        typeof rawSpan.end === 'number' &&
        Number.isSafeInteger(rawSpan.end) &&
        rawSpan.end <= revision.chars &&
        rawSpan.end - rawSpan.start === rawSpan.excerpt.length
      ) {
        span = {
          origin: rawSpan.origin,
          originId: rawSpan.originId,
          revision,
          start: rawSpan.start,
          end: rawSpan.end,
          excerpt: rawSpan.excerpt,
          locator: rawSpan.locator,
        }
      }
    }
    seen.add(row.field)
    out.push({
      version: 1,
      field: row.field as ScopeField,
      claimRevision,
      valueRevision,
      span,
      extraction: 'model-proposal',
      review: 'unreviewed',
    })
  }
  return out
}

export type FieldContinuityStatus =
  | 'anchored'
  | 'missing-origin'
  | 'missing-material'
  | 'changed-material'
  | 'stale-claim'
  | 'stale-field'
  | 'invalid-span'

export interface FieldContinuity {
  field: ScopeField
  status: FieldContinuityStatus
  origin: ScopeFieldOrigin | null
}

function scopeFields(session: Session, nodeId: string, scope: ClaimScope): FieldContinuity[] {
  const claim = session.nodes[nodeId]?.claim ?? ''
  const origins = new Map(
    (normalizeScopeFieldOrigins(scope.fieldOrigins) ?? []).map((row) => [row.field, row]),
  )
  return populatedFields(scope).map((field) => {
    const origin = origins.get(field) ?? null
    let status: FieldContinuityStatus = 'missing-origin'
    if (origin) {
      if (!same(origin.claimRevision, contentRevision(claim))) status = 'stale-claim'
      else if (!same(origin.valueRevision, revisionOf(fieldValue(scope, field))))
        status = 'stale-field'
      else if (origin.span) {
        const span = origin.span
        const candidates =
          span.origin === 'claim'
            ? span.originId === nodeId
              ? [claim]
              : []
            : (session.research?.auditReceipts ?? [])
                .filter((receipt) => receipt.opId === span.originId)
                .flatMap((receipt) =>
                  receipt.prompts
                    .filter((prompt) => prompt.role === 'user')
                    .map((prompt) => prompt.content),
                )
        const material = candidates.find((text) => same(contentRevision(text), span.revision))
        status =
          material === undefined
            ? candidates.length
              ? 'changed-material'
              : 'missing-material'
            : material.slice(span.start, span.end) === span.excerpt
              ? 'anchored'
              : 'invalid-span'
      }
    }
    return { field, status, origin }
  })
}

export interface RetainedEvidenceContext {
  key: string
  bindingId: string
  recordId: string
  claimRevision: ContentRevision | null
  /** Revision of the retained representation, not a promise that the complete page survives. */
  evidenceRevision: ContentRevision
  sourceSnapshotHashes: string[]
  foldedTextSignature: string | null
  span: RetainedSpan | null
  locator: string
  observedAt: number
  acquiredAt: number | null
  stance: 'supports' | 'disputes' | 'context'
  admission: 'returned-citation' | 'manual-binding'
  sourceOpened: boolean | null
  completeTextRetained: boolean
  quotationMatched: boolean | null
  interpretation: 'model-proposal' | 'person-authored'
  review: 'not-recorded'
  notice: 'retracted' | 'flagged' | null
  limitations: string[]
}

function quoteSpan(
  origin: RetainedSpan['origin'],
  originId: string,
  text: string,
  locator: string,
): RetainedSpan | null {
  return text
    ? {
        origin,
        originId,
        revision: contentRevision(text),
        start: 0,
        end: text.length,
        excerpt: text,
        locator,
      }
    : null
}

function retainedEvidence(session: Session, nodeId: string): RetainedEvidenceContext[] {
  const node = session.nodes[nodeId]
  if (!node) return []
  const check = node.verification
  // A legacy display snapshot may have been clipped while its folded hash
  // still describes the full input. Never label that prefix an exact revision.
  const checkedClaimRevision =
    check?.claimAtCheck &&
    (!check.claimHashAtCheck || textHash(foldText(check.claimAtCheck)) === check.claimHashAtCheck)
      ? contentRevision(check.claimAtCheck)
      : null
  const rows: RetainedEvidenceContext[] = []
  const receipt = session.research?.auditReceipts.find(
    (row) => row.opId === check?.opId && row.kind === 'source-check',
  )
  const snapshots = (recordId: string) => {
    const hashes = new Set(
      (receipt?.citations ?? [])
        .filter((row) => row.decision === 'admitted' && row.evidenceRecordId === recordId)
        .flatMap((row) => (row.sourceSnapshotHash ? [row.sourceSnapshotHash] : [])),
    )
    const matching = (receipt?.sourceSnapshots ?? []).filter((row) => hashes.has(row.sha256))
    return {
      sourceSnapshotHashes: [...hashes].sort(),
      acquiredAt: matching.length ? Math.max(...matching.map((row) => row.capturedAt)) : null,
    }
  }
  for (const source of check?.sources ?? []) {
    const recordId = webEvidenceRecordId(source.url)
    const bindingId = evidenceBindingId(nodeId, check!, recordId)
    rows.push({
      key: recordId,
      bindingId,
      recordId,
      claimRevision: checkedClaimRevision,
      evidenceRevision: revisionOf(source),
      ...snapshots(recordId),
      foldedTextSignature: source.textHash ?? null,
      span: quoteSpan('web-quote', bindingId, source.quote, source.url),
      locator: source.url,
      observedAt: check!.checkedAt,
      stance: source.stance,
      admission: 'returned-citation',
      sourceOpened: source.via === 'fetch',
      completeTextRetained: false,
      quotationMatched: source.quoteFound ?? null,
      interpretation: 'model-proposal',
      review: 'not-recorded',
      notice: null,
      limitations: [
        'Only the retained quotation and page signatures survive this check; the full page is not retained.',
        'Unobserved changes to the remote source are unknown.',
      ],
    })
  }
  for (const cite of check?.corpus?.cites ?? []) {
    const recordId = corpusEvidenceRecordId(cite.docId)
    const bindingId = evidenceBindingId(nodeId, check!, recordId)
    const locator = `${cite.docId}#passage-${cite.passage}`
    rows.push({
      key: `${recordId}#${cite.passage}`,
      bindingId,
      recordId,
      claimRevision: checkedClaimRevision,
      evidenceRevision: revisionOf(cite),
      ...snapshots(recordId),
      foldedTextSignature: null,
      span: quoteSpan('corpus-quote', bindingId, cite.quote, locator),
      locator,
      observedAt: check!.checkedAt,
      stance:
        check!.reading === 'supported'
          ? 'supports'
          : check!.reading === 'refuted'
            ? 'disputes'
            : 'context',
      admission: 'returned-citation',
      sourceOpened: null,
      completeTextRetained: false,
      quotationMatched: true,
      interpretation: 'model-proposal',
      review: 'not-recorded',
      notice: cite.notice ?? null,
      limitations: [
        'This is the client-retained passage opening; the complete document is not stored in the exploration.',
        'The check reading applies to the pass; it does not independently classify this passage.',
      ],
    })
  }
  for (const binding of session.research?.manualEvidenceBindings ?? []) {
    if (binding.nodeId !== nodeId) continue
    const record = session.research?.evidenceRecords.find((row) => row.id === binding.recordId)
    if (!record) continue
    rows.push({
      key: binding.id,
      bindingId: binding.id,
      recordId: record.id,
      claimRevision: null,
      evidenceRevision: revisionOf({
        record,
        stance: binding.stance,
        contentScope: binding.contentScope,
      }),
      sourceSnapshotHashes: [],
      foldedTextSignature: record.textHash ?? null,
      span: quoteSpan('evidence-record', record.id, record.text ?? '', record.url ?? record.id),
      locator: record.url ?? record.id,
      observedAt: binding.updatedAt,
      acquiredAt: null,
      stance: binding.stance,
      admission: 'manual-binding',
      sourceOpened: null,
      completeTextRetained: Boolean(record.text && binding.contentScope === 'full-text'),
      quotationMatched: null,
      interpretation: 'person-authored',
      review: 'not-recorded',
      notice: null,
      limitations: [
        'This explicit manual binding does not record the claim revision at binding time or independent interpretation review.',
      ],
    })
  }
  const occurrences = new Map<string, number>()
  return rows.map((row) => {
    const count = (occurrences.get(row.key) ?? 0) + 1
    occurrences.set(row.key, count)
    return {
      ...row,
      // A retained quotation can stay unchanged while the acquired source
      // bytes change. Exact receipt hashes therefore participate in freshness.
      evidenceRevision: row.sourceSnapshotHashes.length
        ? revisionOf({
            representation: row.evidenceRevision,
            sourceSnapshotHashes: row.sourceSnapshotHashes,
          })
        : row.evidenceRevision,
      key: count === 1 ? row.key : `${row.key}#occurrence-${count}`,
    }
  })
}

export interface ClaimEvidenceContext {
  nodeId: string
  claim: string
  claimRevision: ContentRevision
  scope: ClaimScope | null
  scopeFields: FieldContinuity[]
  evidence: RetainedEvidenceContext[]
  model: ModelStamp | null
  reading: SourceReading | null
  outcome: Session['nodes'][string]['outcome'] | null
  gaps: string[]
}

/** Supporting and challenging excerpts travel together with qualifications and unknowns. */
export function claimEvidenceContext(
  session: Session,
  nodeId: string,
): ClaimEvidenceContext | null {
  const node = session.nodes[nodeId]
  if (!node) return null
  const row = session.research?.claimScopes.find((row) => row.nodeId === nodeId)
  const evidence = retainedEvidence(session, nodeId)
  const fields = row ? scopeFields(session, nodeId, row.scope) : []
  const gaps: string[] = []
  if (!node.verification) gaps.push('No current source check is recorded.')
  else if (!verificationLive(node))
    gaps.push('The retained source check predates the current claim wording.')
  if (evidence.some((row) => row.claimRevision === null))
    gaps.push('Some bindings do not retain their exact claim revision.')
  if (fields.some((field) => field.status !== 'anchored'))
    gaps.push('Some extracted scope fields have missing or stale material origins.')
  // The card's two rules: the refuted claim this one stands on is named in the
  // card's words, and a changed record it already names is not listed again.
  const refuted = refutedUnder(session, nodeId)
  if (
    pendingEvidenceStaleOf(session, nodeId)?.changes.some(
      (change) => change.sourceId !== refuted?.source.id,
    )
  )
    gaps.push('Changed evidence still has dependent reasoning awaiting an explicit revisit.')
  if (refuted) gaps.push(`${STATUS_WORD.standsOnRefuted}. ${refutedUnderSentence(refuted)}`)
  if (evidence.some((row) => row.notice === 'retracted'))
    gaps.push(
      `${STATUS_WORD.citesRetracted}: Crossref’s retraction data reports a work this claim’s evidence cites as withdrawn. That is the publisher withdrawing the record, not a verdict on the claim.`,
    )
  if (evidence.some((row) => row.notice === 'flagged'))
    gaps.push(
      'A cited work carries a recorded publisher update or concern notice; its effect needs inspection.',
    )
  return {
    nodeId,
    claim: node.claim,
    claimRevision: contentRevision(node.claim),
    scope: row?.scope ?? null,
    scopeFields: fields,
    evidence,
    model: row?.model ?? null,
    reading: node.verification?.reading ?? null,
    outcome: node.outcome ?? null,
    gaps,
  }
}

export interface ClaimContinuityReference {
  nodeId: string
  claimRevision: ContentRevision
  derivationRevision: ContentRevision
  scopeRevision: ContentRevision | null
  verificationRevision: ContentRevision | null
  outcomeRevision: ContentRevision | null
  parentIds: string[]
  evidence: Array<{
    key: string
    recordId: string
    revision: ContentRevision
    claimRevision: ContentRevision | null
    notice: RetainedEvidenceContext['notice']
  }>
  missingRecordIds: string[]
}

export interface EvidenceContinuitySnapshot {
  version: typeof EVIDENCE_CONTINUITY_VERSION
  sessionId: string
  claims: ClaimContinuityReference[]
}

/** Import references only. A malformed or foreign-version baseline proves nothing about freshness. */
export function parseEvidenceContinuitySnapshot(value: unknown): EvidenceContinuitySnapshot | null {
  const root = object(value)
  const id = (value: unknown): value is string =>
    typeof value === 'string' && value.length > 0 && value.length <= 1000
  const ids = (value: unknown): value is string[] =>
    Array.isArray(value) &&
    value.length <= 10000 &&
    value.every(id) &&
    new Set(value).size === value.length
  if (
    root?.version !== 1 ||
    !id(root.sessionId) ||
    !Array.isArray(root.claims) ||
    root.claims.length > 10000
  )
    return null
  const claims: ClaimContinuityReference[] = []
  const seenClaims = new Set<string>()
  let references = 0
  for (const raw of root.claims) {
    const row = object(raw)
    if (
      !row ||
      !id(row.nodeId) ||
      seenClaims.has(row.nodeId) ||
      !ids(row.parentIds) ||
      !ids(row.missingRecordIds) ||
      !Array.isArray(row.evidence) ||
      row.evidence.length > 10000
    )
      return null
    seenClaims.add(row.nodeId)
    references += row.parentIds.length + row.evidence.length + row.missingRecordIds.length
    if (references > 100000) return null
    const claimRevision = parsedRevision(row.claimRevision)
    const derivationRevision = parsedRevision(row.derivationRevision)
    const scopeRevision = row.scopeRevision === null ? null : parsedRevision(row.scopeRevision)
    const verificationRevision =
      row.verificationRevision === null ? null : parsedRevision(row.verificationRevision)
    const outcomeRevision =
      row.outcomeRevision === null ? null : parsedRevision(row.outcomeRevision)
    if (
      !claimRevision ||
      !derivationRevision ||
      (row.scopeRevision !== null && !scopeRevision) ||
      (row.verificationRevision !== null && !verificationRevision) ||
      (row.outcomeRevision !== null && !outcomeRevision)
    )
      return null
    const evidence: ClaimContinuityReference['evidence'] = []
    const seenEvidence = new Set<string>()
    for (const rawEvidence of row.evidence) {
      const item = object(rawEvidence)
      if (
        !item ||
        !id(item.key) ||
        !id(item.recordId) ||
        seenEvidence.has(item.key) ||
        (item.notice !== null && item.notice !== 'retracted' && item.notice !== 'flagged')
      )
        return null
      seenEvidence.add(item.key)
      const revision = parsedRevision(item.revision)
      const bindingClaim = item.claimRevision === null ? null : parsedRevision(item.claimRevision)
      if (!revision || (item.claimRevision !== null && !bindingClaim)) return null
      evidence.push({
        key: item.key,
        recordId: item.recordId,
        revision,
        claimRevision: bindingClaim,
        notice: item.notice,
      })
    }
    claims.push({
      nodeId: row.nodeId,
      claimRevision,
      derivationRevision,
      scopeRevision,
      verificationRevision,
      outcomeRevision,
      parentIds: [...row.parentIds],
      missingRecordIds: [...row.missingRecordIds],
      evidence,
    })
  }
  return { version: 1, sessionId: root.sessionId, claims }
}

/** Compact, serializable input references suitable for a local agenda or exported comparison. */
export function evidenceContinuitySnapshot(
  session: Session,
  nodeIds: readonly string[] = session.order,
): EvidenceContinuitySnapshot {
  return {
    version: 1,
    sessionId: session.id,
    claims: [...new Set(nodeIds)].flatMap((nodeId) => {
      const node = session.nodes[nodeId]
      if (!node) return []
      const scope = session.research?.claimScopes.find((row) => row.nodeId === nodeId)?.scope
      const check = node.verification
      return [
        {
          nodeId,
          claimRevision: contentRevision(node.claim),
          derivationRevision: revisionOf({
            reasoning: node.reasoning,
            evidence: node.evidence,
            foreignFrom: node.foreignFrom,
          }),
          scopeRevision: scope ? revisionOf(scope) : null,
          verificationRevision: check
            ? revisionOf({
                reading: check.reading,
                scope: check.scope,
                partial: check.partial,
                claimAtCheck: check.claimAtCheck,
                queries: check.queries,
                failures: check.failures,
                seen: check.seen,
                dropped: check.dropped,
                priorArt: check.priorArt,
                corpus: check.corpus,
              })
            : null,
          outcomeRevision: node.outcome ? revisionOf(node.outcome) : null,
          parentIds: [
            ...new Set([...(node.parentId ? [node.parentId] : []), ...(node.bridgeFrom ?? [])]),
          ].sort(),
          evidence: retainedEvidence(session, nodeId)
            .map((row) => ({
              key: row.key,
              recordId: row.recordId,
              revision: row.evidenceRevision,
              claimRevision: row.claimRevision,
              notice: row.notice,
            }))
            .sort((a, b) => a.key.localeCompare(b.key)),
          missingRecordIds: (session.research?.manualEvidenceBindings ?? [])
            .filter(
              (row) =>
                row.nodeId === nodeId &&
                !session.research?.evidenceRecords.some((record) => record.id === row.recordId),
            )
            .map((row) => row.recordId)
            .sort(),
        },
      ]
    }),
  }
}

export type EvidenceUpdateReason =
  | 'baseline-missing'
  | 'claim-removed'
  | 'claim-changed'
  | 'derivation-changed'
  | 'scope-changed'
  | 'source-changed'
  | 'source-removed'
  | 'source-added'
  | 'verification-changed'
  | 'outcome-changed'
  | 'record-missing'
  | 'binding-claim-unknown'
  | 'binding-claim-stale'
  | 'scope-origin-stale'
  | 'publisher-withdrawal'
  | 'publisher-update'
  | 'premise-changed'
  | 'premise-missing'
  | 'pending-evidence-rebase'
  | 'inherited-refutation'

export interface ClaimEvidenceUpdate {
  nodeId: string
  freshness: 'unchanged' | 'changed' | 'unknown' | 'removed'
  action: 'preserve' | 'inspect-missing' | 'review-evidence' | 'revisit-derivation'
  reasons: EvidenceUpdateReason[]
  affectedRecordIds: string[]
  affectedPremiseIds: string[]
}

export interface EvidenceUpdatePlan {
  version: typeof EVIDENCE_CONTINUITY_VERSION
  claims: ClaimEvidenceUpdate[]
  preservedNodeIds: string[]
  affectedNodeIds: string[]
  limitations: string[]
}

/** Data only: nothing here performs a revisit, marks evidence refuted, or modifies unaffected work. */
export function planEvidenceUpdates(baseline: unknown, session: Session): EvidenceUpdatePlan {
  const previous = parseEvidenceContinuitySnapshot(baseline)
  const current = evidenceContinuitySnapshot(session)
  const compatible = previous?.version === 1 && previous.sessionId === session.id
  const old = new Map((compatible ? previous.claims : []).map((row) => [row.nodeId, row]))
  const now = new Map(current.claims.map((row) => [row.nodeId, row]))
  const results = new Map<string, ClaimEvidenceUpdate>()
  for (const nodeId of [...new Set([...old.keys(), ...now.keys()])]) {
    const before = old.get(nodeId)
    const after = now.get(nodeId)
    const reasons = new Set<EvidenceUpdateReason>()
    const records = new Set<string>()
    if (!before) reasons.add('baseline-missing')
    if (!after) reasons.add('claim-removed')
    if (before && after) {
      if (!same(before.claimRevision, after.claimRevision)) reasons.add('claim-changed')
      if (
        !same(before.derivationRevision, after.derivationRevision) ||
        !same(before.parentIds, after.parentIds)
      )
        reasons.add('derivation-changed')
      if (!same(before.scopeRevision, after.scopeRevision)) reasons.add('scope-changed')
      if (!same(before.verificationRevision, after.verificationRevision))
        reasons.add('verification-changed')
      if (!same(before.outcomeRevision, after.outcomeRevision)) reasons.add('outcome-changed')
      const left = new Map(before.evidence.map((row) => [row.key, row]))
      const right = new Map(after.evidence.map((row) => [row.key, row]))
      for (const key of new Set([...left.keys(), ...right.keys()])) {
        const a = left.get(key)
        const b = right.get(key)
        if (!a || !b || !same(a.revision, b.revision)) {
          reasons.add(!a ? 'source-added' : !b ? 'source-removed' : 'source-changed')
          records.add((a ?? b)!.recordId)
        }
      }
    }
    if (after) {
      for (const id of after.missingRecordIds) {
        reasons.add('record-missing')
        records.add(id)
      }
      for (const row of after.evidence) {
        if (!row.claimRevision) reasons.add('binding-claim-unknown')
        else if (!same(row.claimRevision, after.claimRevision)) reasons.add('binding-claim-stale')
        if (row.notice) {
          reasons.add(row.notice === 'retracted' ? 'publisher-withdrawal' : 'publisher-update')
          records.add(row.recordId)
        }
      }
      const scope = session.research?.claimScopes.find((row) => row.nodeId === nodeId)?.scope
      if (scope && scopeFields(session, nodeId, scope).some((row) => row.status !== 'anchored'))
        reasons.add('scope-origin-stale')
      const pending = pendingEvidenceStaleOf(session, nodeId)
      if (pending) {
        reasons.add('pending-evidence-rebase')
        for (const change of pending.changes)
          for (const id of change.evidenceRecordIds) records.add(id)
      }
      if (taintOf(session, nodeId)) reasons.add('inherited-refutation')
    }
    results.set(nodeId, {
      nodeId,
      freshness: !after ? 'removed' : !before ? 'unknown' : reasons.size ? 'changed' : 'unchanged',
      action: 'preserve',
      reasons: [...reasons],
      affectedRecordIds: [...records].sort(),
      affectedPremiseIds: [],
    })
  }
  // Explicit graph dependencies only. Rebase already handles evidence-family
  // sharing; this traversal adds claim/scope/outcome changes between snapshots.
  for (const [nodeId, row] of results) {
    const target = now.get(nodeId)
    if (!target || target.outcomeRevision) continue
    const stack = [...target.parentIds]
    const seen = new Set<string>([nodeId])
    while (stack.length) {
      const parentId = stack.pop()!
      if (seen.has(parentId)) continue
      seen.add(parentId)
      const parent = now.get(parentId)
      const update = results.get(parentId)
      if (!parent) {
        row.reasons.push('premise-missing')
        row.affectedPremiseIds.push(parentId)
      } else {
        if (update?.reasons.length) {
          row.reasons.push('premise-changed')
          row.affectedPremiseIds.push(parentId)
        }
        if (!parent.outcomeRevision) stack.push(...parent.parentIds)
      }
    }
  }
  for (const row of results.values()) {
    row.reasons = [...new Set(row.reasons)]
    row.affectedPremiseIds.sort()
    if (row.freshness !== 'removed' && row.reasons.length) {
      const unknown = row.reasons.some((reason) =>
        [
          'baseline-missing',
          'record-missing',
          'binding-claim-unknown',
          'scope-origin-stale',
          'premise-missing',
        ].includes(reason),
      )
      row.freshness = unknown ? 'unknown' : 'changed'
    }
    row.action =
      row.freshness === 'removed' || row.freshness === 'unknown'
        ? 'inspect-missing'
        : row.reasons.some((reason) =>
              [
                'claim-changed',
                'derivation-changed',
                'scope-changed',
                'premise-changed',
                'pending-evidence-rebase',
                'inherited-refutation',
              ].includes(reason),
            )
          ? 'revisit-derivation'
          : row.reasons.length
            ? 'review-evidence'
            : 'preserve'
  }
  const claims = [...results.values()]
  return {
    version: 1,
    claims,
    preservedNodeIds: claims.filter((row) => row.action === 'preserve').map((row) => row.nodeId),
    affectedNodeIds: claims.filter((row) => row.action !== 'preserve').map((row) => row.nodeId),
    limitations: [
      'Unchanged means the retained inputs match; it does not establish interpretation validity or detect unobserved remote changes.',
      'Missing claim revisions, source material, or extraction origins remain unknown.',
    ],
  }
}
